@if (versions.length > 0) {
  <div class="version-picker">
    <label class="section-label" i18n="@@modelTester.version">Версия</label>
    <div class="version-picker-row">
      <p-select
        [(ngModel)]="selectedTestVersionId"
        [options]="versions"
        [disabled]="versionPickerDisabled"
        optionLabel="name"
        optionValue="id"
        (onChange)="onTestVersionChange()"
        styleClass="w-full">
      </p-select>
      @if (showPropertiesEdit) {
        <p-button
          i18n-label="@@modelTester.editProperties"
          label="Edit properties"
          icon="pi pi-cog"
          severity="secondary"
          [outlined]="true"
          [disabled]="!selectedTestVersionId"
          (click)="onPropertiesEditClick()">
        </p-button>
      }
    </div>
    @if (currentLatestBenchmark; as snap) {
      <div class="benchmark-snapshot-inline tester-benchmark">
        <span class="bench-line"
              i18n-pTooltip="@@modelSelector.benchmark.tooltip.recordedAt"
              pTooltip="Date of the last benchmark run"
              tooltipPosition="top">
          <i class="pi pi-clock"></i>
          <span>{{ formatBenchmarkAt(snap.recorded_at) || '—' }}</span>
        </span>
        @if (currentBenchmarkTypeCode === 'llm') {
          <span class="bench-line"
                i18n-pTooltip="@@modelSelector.benchmark.tooltip.tokensPerSec"
                pTooltip="Average generation speed (tokens per second) across the benchmark"
                tooltipPosition="top">
            <i class="pi pi-gauge"></i>
            <span>{{ formatTokensPerSec(snap.avg_tokens_per_sec) }}</span>
          </span>
          <span class="bench-line"
                i18n-pTooltip="@@modelSelector.benchmark.tooltip.ttft"
                pTooltip="Average time to first token across the benchmark"
                tooltipPosition="top">
            <i class="pi pi-bolt"></i>
            <span>{{ formatTtftMs(snap.avg_ttft_ms) }}</span>
          </span>
        }
        @if (currentBenchmarkTypeCode === 'stt') {
          <span class="bench-line"
                i18n-pTooltip="@@modelSelector.benchmark.tooltip.wer"
                pTooltip="Word Error Rate — доля ошибок распознавания в словах, ниже — лучше"
                tooltipPosition="top">
            <i class="pi pi-percentage"></i>
            <span>WER {{ formatErrorRate(snap.wer) }}</span>
          </span>
          <span class="bench-line"
                i18n-pTooltip="@@modelSelector.benchmark.tooltip.cer"
                pTooltip="Character Error Rate — доля ошибок в символах"
                tooltipPosition="top">
            <i class="pi pi-percentage"></i>
            <span>CER {{ formatErrorRate(snap.cer) }}</span>
          </span>
          <span class="bench-line"
                i18n-pTooltip="@@modelSelector.benchmark.tooltip.rtfx"
                pTooltip="RTFx — скорость обработки относительно длительности аудио"
                tooltipPosition="top">
            <i class="pi pi-bolt"></i>
            <span>{{ formatRtfx(snap.avg_rtfx) }}</span>
          </span>
        }
      </div>
    }
  </div>
}

<!-- Settings (model additional properties) — collapsed by default, sent with the request.
     Schema грузится точечно через GET /api/Models/versions/{id} (SKN-313).
     Показываем состояние: loading spinner, error+retry, либо accordion. -->
<ng-template #paramsAccordion>
  @if (schemaState() === 'loading') {
    <div class="schema-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span i18n="@@modelTester.schema.loading">Загружаем параметры модели...</span>
    </div>
  } @else if (schemaState() === 'error') {
    <div class="schema-error">
      <i class="pi pi-exclamation-triangle"></i>
      <span i18n="@@modelTester.schema.error">Не удалось загрузить дополнительные параметры модели.</span>
      <p-button
        icon="pi pi-refresh"
        severity="secondary"
        [outlined]="true"
        size="small"
        (click)="retryVersionSchema()"
        i18n-label="@@modelTester.schema.retry"
        label="Повторить">
      </p-button>
    </div>
  } @else if (currentAdditionalProperties) {
    <p-accordion [(value)]="parametersAccordionValue" [multiple]="true" class="params-accordion">
      <p-accordion-panel value="params">
        <p-accordion-header i18n="@@modelTester.additionalProperties">Additional properties</p-accordion-header>
        <p-accordion-content>
          <app-properties-edit
            [schemas]="[currentAdditionalProperties]"
            [values]="parameters"
            (valuesChange)="onParametersChange($event)">
          </app-properties-edit>
        </p-accordion-content>
      </p-accordion-panel>
    </p-accordion>
  }
</ng-template>

@if (isVad) {
  <!-- VAD "тестер": интерактивно натестить detection нельзя (нет ни звука
       ни чата) — показываем только version-picker (сверху) + accordion
       со свойствами модели. Значения сохраняются наверх через
       propertiesEditRequested / выбор из тестера. -->
  <div class="tester-section vad-tester">
    <ng-container [ngTemplateOutlet]="paramsAccordion" />
  </div>
}

@if (isVoiceBased) {
  <div class="tester-section tts-tester">
    <div class="filter-field">
      <label class="section-label" i18n="@@modelTester.language">Язык</label>
      <p-select
        [(ngModel)]="selectedLanguageCode"
        [options]="languageOptions"
        [disabled]="languageDisabled"
        optionLabel="label"
        optionValue="value"
        i18n-placeholder="@@modelTester.selectLanguage"
        placeholder="Выберите язык"
        [filter]="true"
        styleClass="w-full">
      </p-select>
    </div>

    <div class="filter-field">
      <label class="section-label" i18n="@@modelTester.voice">Голос</label>
      <app-voice-picker
        [modelVersionId]="selectedTestVersionId"
        [selectedVoiceId]="selectedTestVoiceId"
        (selectedVoiceIdChange)="onTestVoiceChange($event)">
      </app-voice-picker>
    </div>

    <ng-container [ngTemplateOutlet]="paramsAccordion" />

    <div class="chat-history">
      @if (ttsHistory.length === 0) {
        <div class="chat-empty" i18n="@@modelTester.tts.empty">Введите текст и сгенерируйте аудио</div>
      }
      @for (item of ttsHistory; track $index) {
        <div class="chat-message" [class.user]="item.role === 'user'" [class.assistant]="item.role === 'assistant'" [class.failed]="item.failed">
          <div class="chat-role">
            @if (item.role === 'user') {
              <span i18n="@@modelTester.llm.user">You</span>
            } @else {
              <span i18n="@@modelTester.llm.assistant">Assistant</span>
              @if (item.generating) {
                <i class="pi pi-spin pi-spinner" style="margin-left: 0.5rem"></i>
              }
            }
          </div>
          @if (item.role === 'user') {
            <div class="chat-content">{{ item.text }}</div>
          } @else if (item.failed) {
            <div class="chat-content" i18n="@@modelTester.tts.error">Audio generation failed</div>
          } @else if (item.generating) {
            <div class="chat-content" i18n="@@modelTester.tts.generating">Generating audio...</div>
          } @else if (item.urls?.length) {
            <div class="audio-list">
              @for (url of item.urls; track url) {
                <app-audio-player [audioUrl]="url" [compact]="true"></app-audio-player>
              }
            </div>
          }
        </div>
      }
    </div>

    <div class="chat-input-row">
      <textarea
        pTextarea
        rows="2"
        [(ngModel)]="ttsText"
        (keydown)="onTtsInputKeydown($event)"
        i18n-placeholder="@@modelTester.tts.placeholder"
        placeholder="Enter text to synthesize"
        class="w-full"></textarea>
      <div class="chat-actions">
        <p-button
          i18n-label="@@modelTester.tts.generate"
          label="Generate audio"
          icon="pi pi-send"
          [loading]="isLoading"
          [disabled]="!ttsText.trim() || !selectedTestVoiceId"
          (click)="generateTts()">
        </p-button>
        <p-button
          i18n-label="@@modelTester.llm.reset"
          label="Reset"
          icon="pi pi-refresh"
          severity="secondary"
          [outlined]="true"
          [disabled]="isLoading || ttsHistory.length === 0"
          (click)="resetTts()">
        </p-button>
      </div>
    </div>
  </div>
}

@if (isStt) {
  <div class="tester-section stt-tester">
    <div class="filter-field">
      <label class="section-label" i18n="@@modelTester.language">Язык</label>
      <p-select
        [(ngModel)]="selectedLanguageCode"
        [options]="languageOptions"
        [disabled]="languageDisabled"
        optionLabel="label"
        optionValue="value"
        i18n-placeholder="@@modelTester.selectLanguage"
        placeholder="Выберите язык"
        [filter]="true"
        styleClass="w-full">
      </p-select>
    </div>

    <ng-container [ngTemplateOutlet]="paramsAccordion" />

    <div class="chat-history">
      @if (sttHistory.length === 0) {
        <div class="chat-empty" i18n="@@modelTester.stt.empty">Запишите аудио или загрузите файл</div>
      }
      @for (item of sttHistory; track $index) {
        <div class="chat-message" [class.user]="item.role === 'user'" [class.assistant]="item.role === 'assistant'" [class.failed]="item.failed">
          <div class="chat-role">
            @if (item.role === 'user') {
              <span i18n="@@modelTester.llm.user">You</span>
            } @else {
              <span i18n="@@modelTester.llm.assistant">Assistant</span>
              @if (item.transcribing) {
                <i class="pi pi-spin pi-spinner" style="margin-left: 0.5rem"></i>
              }
            }
          </div>
          @if (item.role === 'user' && item.audioUrl) {
            <app-audio-player [audioUrl]="item.audioUrl" [compact]="true"></app-audio-player>
          } @else if (item.failed) {
            <div class="chat-content" i18n="@@modelTester.stt.error">Ошибка распознавания</div>
          } @else if (item.transcribing) {
            <div class="chat-content" i18n="@@modelTester.stt.transcribing">Распознавание...</div>
          } @else {
            <div class="chat-content">{{ item.text }}</div>
          }
        </div>
      }
    </div>

    <div class="chat-input-row">
      @if (sttDraftUrl) {
        <div class="stt-draft">
          <app-audio-player [audioUrl]="sttDraftUrl" [compact]="true"></app-audio-player>
          <p-button
            icon="pi pi-times"
            severity="secondary"
            [text]="true"
            [rounded]="true"
            i18n-pTooltip="@@modelTester.stt.discard"
            pTooltip="Удалить запись"
            (click)="discardDraft()"
            i18n-ariaLabel="@@modelTester.stt.discard"
            ariaLabel="Удалить запись">
          </p-button>
        </div>
      }

      <div class="chat-actions">
        @if (!sttRecording) {
          <p-button
            i18n-label="@@modelTester.stt.recordMic"
            label="Record"
            icon="pi pi-microphone"
            severity="secondary"
            [outlined]="true"
            [disabled]="isLoading"
            (click)="startRecording()">
          </p-button>
        } @else {
          <p-button
            i18n-label="@@modelTester.stt.stopRecording"
            label="Stop"
            icon="pi pi-stop-circle"
            severity="danger"
            (click)="stopRecording()">
          </p-button>
        }

        <p-button
          i18n-label="@@modelTester.stt.uploadFile"
          label="Upload"
          icon="pi pi-upload"
          severity="secondary"
          [outlined]="true"
          [disabled]="sttRecording || isLoading"
          (click)="sttFileInput.click()">
        </p-button>
        <input #sttFileInput type="file" accept="audio/*" (change)="onSttFileSelected($event)" hidden>

        <p-button
          i18n-label="@@modelTester.stt.send"
          label="Send"
          icon="pi pi-send"
          [loading]="isLoading"
          [disabled]="!sttCanSend"
          (click)="sendStt()">
        </p-button>

        <p-button
          i18n-label="@@modelTester.llm.reset"
          label="Reset"
          icon="pi pi-refresh"
          severity="secondary"
          [outlined]="true"
          [disabled]="isLoading || sttHistory.length === 0"
          (click)="resetStt()">
        </p-button>
      </div>
    </div>
  </div>
}

@if (isLlm) {
  <div class="tester-section llm-tester">
    <div class="llm-header">
      <div class="llm-temperature-row">
        <label class="section-label">
          @if (llmTemperatureDisabled) {
            <ng-container i18n="@@modelTester.llm.temperatureDisabled">Temperature: default</ng-container>
          } @else {
            <span i18n="@@modelTester.llm.temperature">Temperature</span>: {{ llmTemperature.toFixed(2) }}
          }
        </label>
        <label class="llm-temperature-toggle">
          <span class="toggle-label" i18n="@@modelTester.llm.temperatureUseDefault">Default</span>
          <p-toggleSwitch
            [(ngModel)]="llmTemperatureDisabled"
            i18n-pTooltip="@@modelTester.llm.temperatureUseDefaultHint"
            pTooltip="Когда включено, температура не задаётся — модель использует значение по умолчанию."
            tooltipPosition="top">
          </p-toggleSwitch>
        </label>
      </div>
      <p-slider
        [(ngModel)]="llmTemperature"
        [min]="0"
        [max]="2"
        [step]="0.05"
        [disabled]="llmTemperatureDisabled"
        styleClass="w-full">
      </p-slider>
      <small class="param-help" i18n="@@modelTester.llm.temperatureHelp">
        Controls output randomness. Lower values give more deterministic answers, higher values — more creative and diverse.
      </small>

      <!-- Top-K: integer slider (1..500). Тот же паттерн default-toggle
           что у temperature. Значение null (disabled) → бэк не передаёт
           параметр в модель. -->
      <div class="llm-temperature-row">
        <label class="section-label">
          @if (llmTopKDisabled) {
            <ng-container i18n="@@modelTester.llm.topKDisabled">Top K: default</ng-container>
          } @else {
            <span i18n="@@modelTester.llm.topK">Top K</span>: {{ llmTopK }}
          }
        </label>
        <label class="llm-temperature-toggle">
          <span class="toggle-label" i18n="@@modelTester.llm.topKUseDefault">Default</span>
          <p-toggleSwitch
            [(ngModel)]="llmTopKDisabled"
            i18n-pTooltip="@@modelTester.llm.topKUseDefaultHint"
            pTooltip="When on, top-K is not set — the model uses its default."
            tooltipPosition="top">
          </p-toggleSwitch>
        </label>
      </div>
      <p-slider
        [(ngModel)]="llmTopK"
        [min]="1"
        [max]="500"
        [step]="1"
        [disabled]="llmTopKDisabled"
        styleClass="w-full">
      </p-slider>
      <small class="param-help" i18n="@@modelTester.llm.topKHelp">
        The model picks the next token from the top-K most probable ones. Lower — more conservative, higher — more diverse.
      </small>

      <!-- Top-P (nucleus): decimal 0..1. -->
      <div class="llm-temperature-row">
        <label class="section-label">
          @if (llmTopPDisabled) {
            <ng-container i18n="@@modelTester.llm.topPDisabled">Top P: default</ng-container>
          } @else {
            <span i18n="@@modelTester.llm.topP">Top P</span>: {{ llmTopP.toFixed(2) }}
          }
        </label>
        <label class="llm-temperature-toggle">
          <span class="toggle-label" i18n="@@modelTester.llm.topPUseDefault">Default</span>
          <p-toggleSwitch
            [(ngModel)]="llmTopPDisabled"
            i18n-pTooltip="@@modelTester.llm.topPUseDefaultHint"
            pTooltip="When on, top-P is not set."
            tooltipPosition="top">
          </p-toggleSwitch>
        </label>
      </div>
      <p-slider
        [(ngModel)]="llmTopP"
        [min]="0"
        [max]="1"
        [step]="0.01"
        [disabled]="llmTopPDisabled"
        styleClass="w-full">
      </p-slider>
      <small class="param-help" i18n="@@modelTester.llm.topPHelp">
        Nucleus sampling: the model considers the smallest token set whose cumulative probability ≥ P. 0.9–0.95 is a good default.
      </small>

      <!-- Max output tokens: отдельный inputNumber с default-toggle.
           Slider'ом не делаем — большой разброс значений, точное число
           важнее (32 vs 32768). -->
      <div class="llm-temperature-row llm-max-tokens-row">
        <label class="section-label">
          @if (llmMaxOutputTokensDisabled) {
            <ng-container i18n="@@modelTester.llm.maxOutputTokensDisabled">Max output tokens: default</ng-container>
          } @else {
            <span i18n="@@modelTester.llm.maxOutputTokens">Max output tokens</span>
          }
        </label>
        <label class="llm-temperature-toggle">
          <span class="toggle-label" i18n="@@modelTester.llm.maxOutputTokensUseDefault">Default</span>
          <p-toggleSwitch
            [(ngModel)]="llmMaxOutputTokensDisabled"
            i18n-pTooltip="@@modelTester.llm.maxOutputTokensUseDefaultHint"
            pTooltip="When on, the output token cap is not overridden."
            tooltipPosition="top">
          </p-toggleSwitch>
        </label>
      </div>
      @if (!llmMaxOutputTokensDisabled) {
        <p-inputNumber
          [(ngModel)]="llmMaxOutputTokens"
          [min]="1"
          [max]="1000000"
          [showButtons]="false"
          i18n-placeholder="@@modelTester.llm.maxOutputTokensPh"
          placeholder="e.g. 4096"
          styleClass="w-full">
        </p-inputNumber>
      }
      <small class="param-help" i18n="@@modelTester.llm.maxOutputTokensHelp">
        Hard cap on the number of tokens the model can produce in a response. Empty — no explicit limit.
      </small>
    </div>

    <ng-container [ngTemplateOutlet]="paramsAccordion" />

    @if (allowCustomLlm) {
      <p-accordion [(value)]="customLlmAccordionValue" [multiple]="true" class="params-accordion">
        <p-accordion-panel value="customHeaders">
          <p-accordion-header i18n="@@modelTester.customLlm.headers">Кастомные HTTP-заголовки</p-accordion-header>
          <p-accordion-content>
            <p class="hint" i18n="@@modelTester.customLlm.headers.hint">
              Задайте поля заголовка и значения по умолчанию — бэкенд использует их при тестовом вызове кастомной LLM.
            </p>
            <app-openapi-properties-editor
              [properties]="customHeadersProps"
              [allowLlmFill]="false"
              [allowedTypes]="['string']"
              i18n-title="@@modelTester.customLlm.headers.schema"
              title="Заголовки"
              [showTitleWhenEmpty]="false"
              (propertiesChange)="onCustomHeadersPropsChange($event)">
            </app-openapi-properties-editor>
          </p-accordion-content>
        </p-accordion-panel>
        <p-accordion-panel value="customBody">
          <p-accordion-header i18n="@@modelTester.customLlm.body">Кастомное тело запроса</p-accordion-header>
          <p-accordion-content>
            <p class="hint" i18n="@@modelTester.customLlm.body.hint">
              Опишите поля тела и значения по умолчанию — бэкенд использует их при тестовом вызове.
            </p>
            <app-openapi-properties-editor
              [properties]="customBodyProps"
              [allowLlmFill]="false"
              i18n-title="@@modelTester.customLlm.body.schema"
              title="Тело"
              [showTitleWhenEmpty]="false"
              (propertiesChange)="onCustomBodyPropsChange($event)">
            </app-openapi-properties-editor>
          </p-accordion-content>
        </p-accordion-panel>
      </p-accordion>
    }

    <div class="chat-history">
      @if (llmMessages.length === 0) {
        <div class="chat-empty" i18n="@@modelTester.llm.empty">Start the dialog by sending a message</div>
      }
      @for (msg of llmMessages; track $index) {
        <div class="chat-message" [class.user]="msg.role === 'user'" [class.assistant]="msg.role === 'assistant'" [class.failed]="msg.failed">
          <div class="chat-role">
            @if (msg.role === 'user') {
              <span i18n="@@modelTester.llm.user">You</span>
            } @else {
              <span i18n="@@modelTester.llm.assistant">Assistant</span>
              @if (msg.isStreaming) {
                <i class="pi pi-spin pi-spinner" style="margin-left: 0.5rem"></i>
              }
            }
          </div>
          <div class="chat-content">{{ msg.content }}</div>
          @if (msg.role === 'assistant' && !msg.isStreaming && (msg.promptTokens != null || msg.completionTokens != null || msg.tokensPerSec != null || msg.ttftMs != null)) {
            <div class="chat-footer">
              <span class="metric-chip"
                    i18n-pTooltip="@@modelTester.llm.tokens.promptTooltip"
                    pTooltip="Tokens used by the prompt"
                    tooltipPosition="top">
                <i class="pi pi-arrow-up"></i>
                <span><ng-container i18n="@@modelTester.llm.tokens.prompt">Prompt</ng-container>: {{ msg.promptTokens || 0 }}</span>
              </span>
              <span class="metric-chip"
                    i18n-pTooltip="@@modelTester.llm.tokens.completionTooltip"
                    pTooltip="Tokens emitted in the response"
                    tooltipPosition="top">
                <i class="pi pi-arrow-down"></i>
                <span><ng-container i18n="@@modelTester.llm.tokens.completion">Completion</ng-container>: {{ msg.completionTokens || 0 }}</span>
              </span>
              @if (msg.tokensPerSec != null) {
                <span class="metric-chip"
                      i18n-pTooltip="@@modelTester.llm.tokens.tokensPerSecTooltip"
                      pTooltip="Generation speed in tokens per second"
                      tooltipPosition="top">
                  <i class="pi pi-gauge"></i>
                  <span>{{ msg.tokensPerSec.toFixed(1) }} t/s</span>
                </span>
              }
              @if (msg.ttftMs != null) {
                <span class="metric-chip"
                      i18n-pTooltip="@@modelTester.llm.tokens.ttftTooltip"
                      pTooltip="Time to first token"
                      tooltipPosition="top">
                  <i class="pi pi-bolt"></i>
                  <span>{{ msg.ttftMs | number:'1.0-0' }} ms</span>
                </span>
              }
            </div>
          }
        </div>
      }
    </div>

    <div class="chat-input-row">
      <textarea
        pTextarea
        rows="2"
        [(ngModel)]="llmInput"
        (keydown)="onLlmInputKeydown($event)"
        i18n-placeholder="@@modelTester.llm.placeholder"
        placeholder="Type a message (Enter to send, Shift+Enter for newline)"
        class="w-full"></textarea>
      <div class="chat-actions">
        <p-button
          i18n-label="@@modelTester.llm.send"
          label="Send"
          icon="pi pi-send"
          [loading]="isLoading"
          [disabled]="!llmInput.trim()"
          (click)="sendLlm()">
        </p-button>
        <p-button
          i18n-label="@@modelTester.llm.reset"
          label="Reset"
          icon="pi pi-refresh"
          severity="secondary"
          [outlined]="true"
          [disabled]="isLoading || llmMessages.length === 0"
          (click)="resetLlm()">
        </p-button>
      </div>
    </div>
  </div>
}

